import React, { useState } from 'react';
import styled from 'styled-components';
import { Cpu, Database, FileText, ShieldCheck, X } from 'lucide-react';

type Tone = 'official' | 'derived' | 'reported' | 'none';
const TONES: Record<Tone, { border: string; bg: string; fg: string }> = {
  official: { border: 'rgba(52, 211, 153, 0.38)', bg: 'rgba(6, 78, 59, 0.24)', fg: '#a7f3d0' },
  derived: { border: 'rgba(96, 165, 250, 0.4)', bg: 'rgba(30, 58, 138, 0.28)', fg: '#bfdbfe' },
  reported: { border: 'rgba(251, 191, 36, 0.38)', bg: 'rgba(120, 53, 15, 0.24)', fg: '#fde68a' },
  none: { border: 'rgba(148, 163, 184, 0.3)', bg: 'rgba(30, 41, 59, 0.4)', fg: '#cbd5e1' },
};
/** FR-8.2: OFFICIAL/APPROVED/ACTIVE/RELEASED read as authority; COMPUTED/SYNCED/DERIVED as derived; REPORTED/EXTRACTED/ENTERED as someone's statement; NONE/NOT_AVAILABLE as absent. */
export const authorityToneOf = (authority: string): Tone => {
  const a = String(authority || '').toUpperCase();
  if (['OFFICIAL', 'APPROVED', 'ACTIVE', 'RELEASED', 'VERIFIED', 'LEGACY_APPROVED'].includes(a)) return 'official';
  if (['COMPUTED', 'SYNCED', 'DERIVED', 'APPROVED_DERIVED'].includes(a)) return 'derived';
  if (['REPORTED', 'EXTRACTED', 'ENTERED'].includes(a)) return 'reported';
  return 'none';
};

const Badge = styled.button<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.27rem 0.46rem;
  border: 1px solid ${p => TONES[p.$tone].border};
  border-radius: 999px;
  background: ${p => TONES[p.$tone].bg};
  color: ${p => TONES[p.$tone].fg};
  cursor: pointer;
  font-size: 0.6rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.68);
`;

const Dialog = styled.div`
  width: min(34rem, 100%);
  max-height: min(42rem, calc(100vh - 2rem));
  overflow: auto;
  padding: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 14px;
  background: #0b1220;
  box-shadow: 0 24px 90px rgba(2, 6, 23, 0.7);
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #f8fafc;
  font-weight: 850;
`;

const LineageGrid = styled.dl`
  display: grid;
  grid-template-columns: minmax(8rem, 0.7fr) minmax(0, 1.3fr);
  gap: 0.55rem 0.8rem;
  margin: 1rem 0 0;
  font-size: 0.72rem;

  dt { color: #94a3b8; }
  dd { margin: 0; overflow-wrap: anywhere; color: #e2e8f0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
`;

const Close = styled.button`
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
`;

const humanize = (value: string) => value
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .replace(/^./, letter => letter.toUpperCase());

export const AuthorityBadge: React.FC<{
  label: string;
  authority: string;
  lineage: Record<string, unknown>;
}> = ({ label, authority, lineage }) => {
  const [open, setOpen] = useState(false);
  const tone = authorityToneOf(authority);
  const available = tone !== 'none';
  const fields = Object.entries(lineage).filter(([, value]) => value !== null && value !== undefined && value !== '');

  return (
    <>
      <Badge $tone={tone} type="button" onClick={() => setOpen(true)}>
        {tone === 'official' ? <ShieldCheck size={12} /> : tone === 'derived' ? <Cpu size={12} /> : tone === 'reported' ? <FileText size={12} /> : <Database size={12} />}
        {label}: {authority.replace(/_/g, ' ')}
      </Badge>
      {open && (
        <Overlay role="presentation" onMouseDown={() => setOpen(false)}>
          <Dialog role="dialog" aria-modal="true" aria-label={`${label} lineage`} onMouseDown={event => event.stopPropagation()}>
            <DialogHeader>
              <span>{label} authority &amp; lineage</span>
              <Close type="button" aria-label="Close lineage" onClick={() => setOpen(false)}><X size={18} /></Close>
            </DialogHeader>
            <LineageGrid>
              {fields.map(([key, value]) => (
                <React.Fragment key={key}>
                  <dt>{humanize(key)}</dt>
                  <dd>{String(value)}</dd>
                </React.Fragment>
              ))}
            </LineageGrid>
          </Dialog>
        </Overlay>
      )}
    </>
  );
};
